import * as myaccount from '@myaccount/pre-integrated-feature';
import * as modals from '@myaccount/modals';
import { StateType } from '@central-products/interface-session-service';
import { workflow as saferGamblingModalsWorkflow } from '@myaccount/safer-gambling-modals';

import { publish, publishSticky, subscribe, unsubscribe } from '@wh/message-bus';
import {
  initWorkflows,
  productMenu,
  subNavStart,
  subNavEnd,
  deposit,
  logo,
} from '@sitebase/pre-integrated-header/default-features';

import { ACCOUNT_ACTION_IDS } from '@central-products/interface-account-actions';

import { initRadio } from '../radio';
import { CHANNEL_ID, DEPOSIT_NORMAL_TYPE } from './consts';
import {
  ACCOUNT_ACTION_REQUESTED,
  HEADER_SEARCH_OPEN,
  SIDEBAR_NAVIGATION_OPEN,
  SIDEBAR_NAVIGATION_CLOSE,
  SESSION_SYNCHRONIZED_EVENT,
  LANGUAGE_ITEM_CLICKED,
  LANGUAGE_SELECT_COMMAND,
  MY_ACCOUNT_OPEN_FEEDBACK,
  FEEDBACK_OPEN_TOPIC,
  MY_ACCOUNT_OPEN_HELP,
  MY_ACCOUNT_OPEN_REWARDS_TOPIC,
  ODDS_FORMAT_CHANGE_TOPIC,
  ODDS_FORMAT_CHANGE_TOPIC_NEW,
  DEPOSIT_MODAL_OPENED,
} from '../../topics';

export const openLink = ({ link = {} } = {}) => {
  if (link.url) {
    const { url, target = '_self' } = link;
    const win = window.open(url, target);
    win.focus();
  }
};

const openLinkFromUrl = (url) => openLink({ link: { url } });

const openLinkFromUrlInNewTab = (url) => openLink({ link: { url, target: '_blank' } });

export const openDeposit = () => {
  publish(deposit.commands.open({ type: DEPOSIT_NORMAL_TYPE }));
};

export const openSearch = () => {
  publish({ topic: HEADER_SEARCH_OPEN });
};

const openFeedback = () => {
  publish({ topic: FEEDBACK_OPEN_TOPIC });
};

export const setupDefaults = ({ locale }) => {
  publish(productMenu.commands.select({ id: CHANNEL_ID }));

  // on tablet, the message is not being parsed because the subscription is made only when one opens the menu,
  // so we need to republish sticky
  publishSticky(LANGUAGE_SELECT_COMMAND, locale);

  initRadio();
};

const forwardOddsFormatChange = ({ data }) => {
  publish({ topic: ODDS_FORMAT_CHANGE_TOPIC_NEW, data });
};

const openLogin = () => {
  publish('wh.common.event.ui.header.login.requested');
};

const openMyAccountPage = (viewUrlParam) => {
  if (viewUrlParam === 'personalDetailsMarketing') {
    publishSticky('wh.myAccount.command.dropdown.open', { path: '/PersonalDetails/Marketing' });
    return;
  }
  publishSticky('wh.myAccount.command.dropdown.open', { path: viewUrlParam });
};

const openAccountPageIfNeeded = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const rawUrl = window.location.toString().split('?')[0];
  const urlparams = new URLSearchParams(window.location.search);
  const viewUrlParam = urlparams.get('myAccountView');
  if (!viewUrlParam) {
    return;
  }
  const sessionStateGet = subscribe({
    topic: 'wh.common.event.session.state.get',
    callback: ({ data }) => {
      if (data.state === 'NOT_LOGGED_IN') {
        openLogin();
      }
    },
  });

  const stateChangedSubscribeId = subscribe({
    topic: 'wh.common.event.sessionService.state.changed',
    callback: ({ data }) => {
      if (data.state !== StateType.SESSION_EXISTS) {
        return;
      }
      unsubscribe(sessionStateGet);
      unsubscribe(stateChangedSubscribeId);
      openMyAccountPage(viewUrlParam);
      urlparams.delete('myAccountView');
      const queryStr = urlparams.toString();
      window.history.pushState(null, '', `${rawUrl}${queryStr ? '?' + queryStr : ''}`);
    },
  });
};

const postRegistrationSubscriptions = () => {
  let depositRequested = false;
  let sessionSynchronized = false;

  const accountActionSubscriptionToken = subscribe({
    topic: ACCOUNT_ACTION_REQUESTED,
    callback: ({ data }) => {
      if (data && data.actionId === ACCOUNT_ACTION_IDS.DEPOSIT_START) {
        depositRequested = true;
        if (sessionSynchronized) {
          openDeposit();
        }
      }
    },
  });

  const sessionSynchronizedSubscriptionToken = subscribe({
    topic: SESSION_SYNCHRONIZED_EVENT,
    callback: ({ data }) => {
      sessionSynchronized = data.sessionSynchronized;
      if (depositRequested) {
        openDeposit();
      }
    },
  });

  const depositModalOpenedToken = subscribe({
    topic: DEPOSIT_MODAL_OPENED,
    callback: () => {
      depositRequested = false;
    },
  });

  return [accountActionSubscriptionToken, sessionSynchronizedSubscriptionToken, depositModalOpenedToken];
};

export const initSubscription = ({
  disableQuickDeposit,
  isMyAccountEnabled,
  links = {},
  onCloseSideMenu,
  onLanguageSwitchRequested,
  onOpenSideMenu,
} = {}) => {
  initWorkflows({ disableQuickDeposit });
  const { promotionsUrl, helpHomeUrl } = links;
  if (isMyAccountEnabled) {
    myaccount.initWorkflow();
    modals.initWorkflow();
    saferGamblingModalsWorkflow();
    openAccountPageIfNeeded();
  }

  const tokensSet = [
    subscribe(subNavStart.events.onRequested(openLink)),
    subscribe(subNavEnd.events.onRequested(openLink)),
    subscribe(logo.events.onRequested(openLink)),
    subscribe({ topic: SIDEBAR_NAVIGATION_CLOSE, callback: onCloseSideMenu }),
    subscribe({ topic: SIDEBAR_NAVIGATION_OPEN, callback: onOpenSideMenu }),
    subscribe({
      topic: LANGUAGE_ITEM_CLICKED,
      // TODO: when site-base mesage-bus-helper will return full envelop remove data destructuring.
      callback: ({ data }) => onLanguageSwitchRequested(data),
    }),
    subscribe({
      topic: MY_ACCOUNT_OPEN_REWARDS_TOPIC,
      callback: () => openLinkFromUrl(promotionsUrl),
    }),
    subscribe({
      topic: MY_ACCOUNT_OPEN_HELP,
      callback: () => openLinkFromUrlInNewTab(helpHomeUrl),
    }),
    subscribe({ topic: MY_ACCOUNT_OPEN_FEEDBACK, callback: openFeedback }),
    subscribe({ topic: ODDS_FORMAT_CHANGE_TOPIC, callback: forwardOddsFormatChange }),
    ...postRegistrationSubscriptions(),
  ];

  return () => {
    tokensSet.forEach(unsubscribe);
  };
};

export default initSubscription;
